"use client"

import { createContext, useContext, useState, useEffect, ReactNode, useRef } from "react"
import { useSession } from "next-auth/react"

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  djangoToken: string | null
  refreshToken: string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)
const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || "https://api-dggs.hartis.org"

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: session, status } = useSession()
  const [djangoToken, setDjangoToken] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)
  
  const [hasMounted, setHasMounted] = useState(false)
  const [initialCheckDone, setInitialCheckDone] = useState(false)
  
  const isExchanging = useRef(false)

  useEffect(() => {
    setHasMounted(true)
    const localJwt = localStorage.getItem("jwt")
    const localRefresh = localStorage.getItem("refresh")
    
    if (localJwt) {
      setDjangoToken(localJwt)
      setRefreshToken(localRefresh)
    }
    setInitialCheckDone(true)
  }, [])

  useEffect(() => {
    if (status === "loading") return

    if (status === "unauthenticated" || session?.error === "RefreshDjangoTokenError") {
      localStorage.removeItem("jwt")
      localStorage.removeItem("refresh")
      setDjangoToken(null)
      setRefreshToken(null)
      return // Removed setIsLoading(false)
    }

    const syncTokens = async () => {
      if (session?.djangoAccess) {
        setDjangoToken(session.djangoAccess)
        setRefreshToken(session.djangoRefresh ?? null)
        localStorage.setItem("jwt", session.djangoAccess)
        if (session.djangoRefresh) localStorage.setItem("refresh", session.djangoRefresh)
        return // Removed setIsLoading(false)
      }

      const localJwt = localStorage.getItem("jwt")
      const localRefresh = localStorage.getItem("refresh")
      if (localJwt) {
        setDjangoToken(localJwt)
        setRefreshToken(localRefresh)
        return // Removed setIsLoading(false)
      }

      if (status === "authenticated" && session?.accessToken && !isExchanging.current) {
        isExchanging.current = true
        try {
          const res = await fetch(`${API_BASE}/api/oidc/exchange/`, {
            method: "POST",
            headers: { Authorization: `Bearer ${session.accessToken}` },
          })
          
          if (res.ok) {
            const data = await res.json()
            if (data?.access) {
              localStorage.setItem("jwt", data.access)
              localStorage.setItem("refresh", data.refresh)
              setDjangoToken(data.access)
              setRefreshToken(data.refresh)
            }
          }
        } catch (error) {
          console.error("Exchange error:", error)
        } finally {
          isExchanging.current = false
        }
      }
    }

    syncTokens()
  }, [session, status])

  // Computed values - No state setter needed
  const isLoading = !hasMounted || !initialCheckDone || (status === "loading" && !djangoToken);
  const isAuthenticated = status === "authenticated" && !!djangoToken;

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        djangoToken,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within AuthProvider")
  }
  return context
}
